import React, { useEffect, useMemo, useState } from 'react';
import { CreditCard, TrendingDown, TrendingUp } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { adminApi } from '../../services/api';

const money = (value) => `Rs. ${Number(value || 0).toLocaleString('en-IN')}`;

const PaymentHistory = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.listPaymentHistory()
      .then((response) => setEntries(response.data?.data || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, []);

  const totals = useMemo(() => entries.reduce((acc, entry) => {
    if (entry.type === 'credit') acc.credit += Number(entry.amount || 0);
    if (entry.type === 'debit') acc.debit += Number(entry.amount || 0);
    return acc;
  }, { credit: 0, debit: 0 }), [entries]);

  const rows = entries.map((entry) => ({
    ...entry,
    id: entry.id,
    date: entry.createdAt ? new Date(entry.createdAt).toLocaleString() : '-',
    order: String(entry.orderId || '').slice(-8).toUpperCase(),
    customer: `${entry.customerName || 'Customer'} ${entry.customerEmail ? `(${entry.customerEmail})` : ''}`,
    amountText: money(entry.amount),
    balanceText: money(entry.balance)
  }));

  const columns = [
    { key: 'date', label: 'Date' },
    { key: 'order', label: 'Order' },
    { key: 'customer', label: 'Customer' },
    {
      key: 'type',
      label: 'Type',
      render: (value) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${value === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {value === 'credit' ? 'Credit' : 'Debit'}
        </span>
      )
    },
    { key: 'amountText', label: 'Amount' },
    { key: 'balanceText', label: 'Balance' },
    { key: 'status', label: 'Status' },
    { key: 'method', label: 'Method' },
    { key: 'note', label: 'Note' }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payment History"
        description="Track order payments, remaining collections, cancellations, and refund debits."
        breadcrumbs={['Home', 'Payments']}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-gray-100 bg-white p-5">
          <div className="flex items-center gap-3">
            <span className="rounded-xl bg-amber-100 p-3 text-amber-700"><CreditCard size={20} /></span>
            <div>
              <p className="text-sm text-gray-500">Transactions</p>
              <p className="text-2xl font-semibold text-gray-900">{entries.length}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-5">
          <div className="flex items-center gap-3">
            <span className="rounded-xl bg-green-100 p-3 text-green-700"><TrendingUp size={20} /></span>
            <div>
              <p className="text-sm text-gray-500">Credits</p>
              <p className="text-2xl font-semibold text-gray-900">{money(totals.credit)}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-5">
          <div className="flex items-center gap-3">
            <span className="rounded-xl bg-red-100 p-3 text-red-700"><TrendingDown size={20} /></span>
            <div>
              <p className="text-sm text-gray-500">Debits / Refunds</p>
              <p className="text-2xl font-semibold text-gray-900">{money(totals.debit)}</p>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-gray-100 bg-white p-8 text-center text-gray-600">Loading payment history...</div>
      ) : (
        <DataTable columns={columns} data={rows} title="All Payment Activity" itemsPerPage={12} />
      )}
    </div>
  );
};

export default PaymentHistory;
